import { Search } from "lucide-react";

import { csvText } from "../lib/csv";

/**
 * Small kit for the data screens: stat plates, a search field and the
 * spreadsheet download.
 */

/** A number worth reading: a bold, evenly spaced figure over its label. */
export function StatPlate({
  value,
  label,
  tone = "ink",
}: {
  value: number | string;
  label: string;
  tone?: "ink" | "amber";
}) {
  return (
    <div className="surface min-w-32 px-5 py-4">
      <p
        className={`stat-value ${
          tone === "amber"
            ? "text-[color:var(--color-amber)]"
            : "text-[color:var(--color-ink)]"
        }`}
      >
        {value}
      </p>
      <p className="mt-2 text-label font-medium text-[color:var(--color-ink-soft)]">
        {label}
      </p>
    </div>
  );
}

export function SearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <label className="flex min-h-11 min-w-56 flex-1 items-center gap-2.5 rounded-md border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-3.5 py-2 transition-colors focus-within:border-[color:var(--color-amber)] md:max-w-md">
      <Search
        size={16}
        aria-hidden="true"
        className="shrink-0 text-[color:var(--color-ink-soft)]"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full bg-transparent text-body text-[color:var(--color-ink)] outline-none placeholder:text-[color:var(--color-ink-faint)]"
      />
    </label>
  );
}

/**
 * Save rows as a spreadsheet file. The link is put on the page for the
 * click and the file's URL is kept for a second afterwards: iOS Safari
 * ignores a click on a link that isn't on the page, and a URL revoked
 * straight after the click can cancel the download.
 */
export function downloadCsv(
  filename: string,
  header: string[],
  rows: (string | number)[][],
) {
  const blob = new Blob([csvText(header, rows)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
